import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import Tooltip from '../components/Tooltip';
import ImagemProduto from '../components/ImagemProduto';

const CRITERIOS_LABELS = {
  venda_liquida: 'Maior Venda Líquida',
  margem_bruta: 'Maior Margem Bruta',
  mkp: 'Maior MKP',
  atendimentos: 'Maior Nº de Atendimentos',
};

// ---- Período de referência (histórico da meta) ----
// Só dia e mês importam: o backend compara com o mesmo dia/mês nos N anos
// anteriores ao da campanha e ignora o ano gravado. Gravamos sempre o ano 2000
// (bissexto, aceita 29/02) só como marcador — ele nunca aparece na tela.
const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mdDe = (iso) => (iso ? String(iso).split('T')[0].slice(5, 10) : '');
const refDeMD = (md) => (md ? `2000-${md}` : '');
const dmBR = (md) => (md ? md.split('-').reverse().join('/') : '');
const listaAnos = (anos) => (anos.length <= 1 ? String(anos[0] ?? '') : `${anos.slice(0, -1).join(', ')} e ${anos[anos.length - 1]}`);
// "Personalizada" = dia/mês diferente do da campanha. Se não for, a referência
// simplesmente acompanha a campanha quando as datas mudam.
const refPersonalizada = (c) => !!(c.periodo_ref_inicio && c.periodo_ref_fim && c.data_inicio && c.data_fim)
  && (mdDe(c.periodo_ref_inicio) !== mdDe(c.data_inicio) || mdDe(c.periodo_ref_fim) !== mdDe(c.data_fim));

// Mesma regra do backend (services/periodoReferencia.js), só pra pré-visualizar.
const resumoReferencia = (f) => {
  if (!f.data_inicio || !f.data_fim) return 'Defina as datas da campanha para ver o período de comparação.';
  const ini = f.ref_personalizada ? mdDe(f.periodo_ref_inicio) : mdDe(f.data_inicio);
  const fim = f.ref_personalizada ? mdDe(f.periodo_ref_fim) : mdDe(f.data_fim);
  if (!ini || !fim) return '';
  const base = parseInt(String(f.data_inicio).slice(0, 4), 10) - 1;
  const anos = Array.from({ length: Math.max(1, parseInt(f.qtd_anos_ref, 10) || 1) }, (_, i) => base - i);
  const cruza = fim < ini; // 'MM-DD' compara bem como texto
  return `Compara com ${dmBR(ini)} a ${dmBR(fim)}${cruza ? ' (termina no ano seguinte)' : ''} de ${listaAnos(anos)}.`;
};

function SeletorDiaMes({ value, onChange, disabled }) {
  const [mm, dd] = (value || '01-01').split('-');
  return (
    <div className="d-flex gap-1">
      <select className="form-select form-select-sm" style={{ width: 70 }} value={dd} disabled={disabled}
        onChange={e => onChange(`${mm}-${e.target.value}`)}>
        {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <select className="form-select form-select-sm" style={{ width: 84 }} value={mm} disabled={disabled}
        onChange={e => onChange(`${e.target.value}-${dd}`)}>
        {MESES_CURTOS.map((n, i) => { const m = String(i + 1).padStart(2, '0'); return <option key={m} value={m}>{n}</option>; })}
      </select>
    </div>
  );
}

export default function CampanhaVendedorDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('campanhas_vendedor'); // Gestor/Marketing: leitura + escrita
  const soLeitura = !podeEditar;                          // Gerente de loja: somente visualiza

  const [camp, setCamp] = useState(null);
  const [aba, setAba] = useState('resultado'); // Dashboard-first
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [regrasPdf, setRegrasPdf] = useState(null);   // status do PDF de regras
  const [subindoPdf, setSubindoPdf] = useState(false);

  const [classesBDenter, setClassesBDenter] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [unidades, setUnidades] = useState([]);

  // Config local
  const [form, setForm] = useState({});
  const [premios, setPremios] = useState([]); // [{ posicao, valor }] — tipo ranking

  // Classes (busca)
  const [classeInput, setClasseInput] = useState('');
  const [classeDropOpen, setClasseDropOpen] = useState(false);

  // Filiais (agrupamento)
  const [filiaisConfig, setFiliaisConfig] = useState([]); // [{ filial_codigo, grupo }]

  // Resultado
  const [resultado, setResultado] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [expandirGrupo, setExpandirGrupo] = useState({}); // { grupo: true/false }

  // Produtos resolvidos pelo filtro (classes/fornecedores)
  const [produtosFiltro, setProdutosFiltro] = useState(null);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  const [excluidosProd, setExcluidosProd] = useState(new Set()); // codpros desmarcados
  const [ordProd, setOrdProd] = useState({ campo: null, dir: 'asc' });
  const [salvandoProd, setSalvandoProd] = useState(false);
  const [buscaProd, setBuscaProd] = useState(''); // filtro rápido da tabela
  const exclTimer = useRef(null);

  // Drill-down: detalhe analítico de um vendedor (modal)
  const [detalheVendedor, setDetalheVendedor] = useState(null); // { codvend, nome, grupo, dados }
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);

  const fmt = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const carregar = useCallback(async () => {
    try {
      const [c, fils, classes] = await Promise.all([
        axios.get(`${API}/campanhas-vendedor/${id}`),
        axios.get(`${API}/filiais`),
        axios.get(`${API}/produtos/classes`),
      ]);
      setCamp(c.data);
      setFiliais(fils.data);
      setClassesBDenter(classes.data);
      setForm({
        nome: c.data.nome,
        fornecedor: c.data.fornecedor || '',
        tipo: c.data.tipo || 'vendedor',
        data_inicio: c.data.data_inicio?.split('T')[0] || '',
        data_fim: c.data.data_fim?.split('T')[0] || '',
        periodo_ref_inicio: c.data.periodo_ref_inicio?.split('T')[0] || '',
        periodo_ref_fim: c.data.periodo_ref_fim?.split('T')[0] || '',
        ref_personalizada: refPersonalizada(c.data),
        qtd_anos_ref: c.data.qtd_anos_ref || 1,
        percentual_meta: c.data.percentual_meta,
        valor_por_unidade: c.data.valor_por_unidade ?? '',
        unidade_premiada: c.data.unidade_premiada || 'M2',
        rankear_por: c.data.rankear_por || 'valor',
        qtd_ganhadores: c.data.qtd_ganhadores || 3,
        premiacao: c.data.premiacao || '',
        observacoes: c.data.observacoes || '',
      });
      setPremios((c.data.premios && c.data.premios.length)
        ? c.data.premios.map(p => ({ posicao: p.posicao, valor: p.valor }))
        : Array.from({ length: c.data.qtd_ganhadores || 3 }, (_, i) => ({ posicao: i + 1, valor: '' })));
      setFiliaisConfig(c.data.filiais.map(f => ({ filial_codigo: f.filial_codigo, grupo: f.grupo })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    axios.get(`${API}/produtos/unidades`).then(res => setUnidades(res.data)).catch(() => setUnidades([]));
  }, []);

  // ---- PDF de regras ----
  const carregarRegrasPdf = useCallback(() => {
    axios.get(`${API}/campanhas-vendedor/${id}/regras-pdf/status`)
      .then(r => setRegrasPdf(r.data)).catch(() => setRegrasPdf(null));
  }, [id]);
  useEffect(() => { carregarRegrasPdf(); }, [carregarRegrasPdf]);

  const baixarRegrasPdf = async () => {
    try {
      const res = await axios.get(`${API}/campanhas-vendedor/${id}/regras-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao abrir o PDF', 'error'); }
  };

  // Apresentação da campanha, gerada na hora, em dois formatos:
  //  - 'pdf': completo, com metas e valores por loja — pro fornecedor;
  //  - 'imagem': mesmo layout numa foto, sem valores de venda nem meta — pros vendedores.
  // Baixa como arquivo (e não abre em aba nova) porque a geração leva alguns segundos e
  // o navegador bloquearia o pop-up.
  const baixarApresentacao = async (formato) => {
    const img = formato === 'imagem';
    try {
      Swal.fire({
        title: img ? 'Gerando a imagem...' : 'Gerando o PDF...',
        text: img ? 'Leva alguns segundos.' : 'Calculando o histórico das lojas — leva alguns segundos.',
        didOpen: () => Swal.showLoading(), allowOutsideClick: false,
      });
      const res = await axios.get(`${API}/campanhas-vendedor/${id}/apresentacao-${img ? 'imagem' : 'pdf'}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: img ? 'image/png' : 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Campanha - ${String(camp?.nome || id).replace(/[\\/:*?"<>|]/g, '-')}${img ? ' (divulgação).png' : '.pdf'}`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      Swal.close();
    } catch (err) {
      // Com responseType 'blob', a mensagem de erro também chega como blob.
      let msg = img ? 'Erro ao gerar a imagem' : 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (e) { /* mantém a genérica */ }
      Swal.fire('Erro', msg, 'error');
    }
  };

  const subirRegrasPdf = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { Swal.fire('Atenção', 'Selecione um arquivo PDF.', 'warning'); return; }
    if (file.size > 1.4 * 1024 * 1024) { Swal.fire('Atenção', 'PDF muito grande (máx ~1,4MB). Comprima e tente de novo.', 'warning'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setSubindoPdf(true);
      try {
        await axios.post(`${API}/campanhas-vendedor/${id}/regras-pdf`, { pdf_base64: reader.result });
        Swal.fire({ icon: 'success', title: 'PDF de regras enviado!', timer: 1400, showConfirmButton: false });
        carregarRegrasPdf();
      } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Não foi possível enviar.', 'error'); }
      finally { setSubindoPdf(false); }
    };
    reader.readAsDataURL(file);
  };

  const removerRegrasPdf = async () => {
    const r = await Swal.fire({ title: 'Remover o PDF de regras?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545' });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/campanhas-vendedor/${id}/regras-pdf`);
      carregarRegrasPdf();
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao remover', 'error'); }
  };

  // Mapa: grupo → "Nome Filial 1 + Nome Filial 2"
  const nomeGrupo = (grupo) => {
    if (!camp?.filiais || !filiais.length) return grupo;
    const codigos = camp.filiais.filter(f => f.grupo === grupo).map(f => f.filial_codigo);
    const nomes = codigos.map(c => filiais.find(f => parseInt(f.codigo) === c)?.nome).filter(Boolean);
    return nomes.length ? nomes.join(' + ') : grupo;
  };

  // Auto-carregar resultado/histórico quando entra
  useEffect(() => {
    if (aba === 'resultado' && camp && !resultado) carregarResultado();
    if (aba === 'metas' && camp && !historico && camp.metas?.length > 0) carregarHistorico();
    // eslint-disable-next-line
  }, [aba, camp]);

  // Produtos do filtro — recarrega ao entrar na aba ou quando classes/fornecedores mudam
  const carregarProdutosFiltro = useCallback(async () => {
    setLoadingProdutos(true);
    try {
      const res = await axios.get(`${API}/campanhas-vendedor/${id}/produtos`);
      setProdutosFiltro(res.data);
      setExcluidosProd(new Set((res.data.produtos || []).filter(p => !p.marcado).map(p => p.codpro)));
    } catch (err) { setProdutosFiltro(null); }
    finally { setLoadingProdutos(false); }
  }, [id]);

  // Salva os desmarcados (excluídos) com debounce
  const salvarExcluidos = (novoSet) => {
    if (exclTimer.current) clearTimeout(exclTimer.current);
    setSalvandoProd(true);
    exclTimer.current = setTimeout(async () => {
      try { await axios.put(`${API}/campanhas-vendedor/${id}/produtos-excluidos`, { excluidos: [...novoSet] }); }
      catch { /* ignora */ }
      finally { setSalvandoProd(false); }
    }, 700);
  };
  const toggleProd = (codpro) => setExcluidosProd(prev => {
    const n = new Set(prev);
    if (n.has(codpro)) n.delete(codpro); else n.add(codpro);
    salvarExcluidos(n); return n;
  });
  const marcarTodosProd = () => { const n = new Set(); setExcluidosProd(n); salvarExcluidos(n); };
  const desmarcarTodosProd = () => { const n = new Set((produtosFiltro?.produtos || []).map(p => p.codpro)); setExcluidosProd(n); salvarExcluidos(n); };
  const toggleOrdProd = (campo) => setOrdProd(prev => ({ campo, dir: prev.campo === campo && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const iconOrd = (campo) => ordProd.campo === campo ? (ordProd.dir === 'asc' ? ' ▲' : ' ▼') : '';

  useEffect(() => {
    if (aba === 'classes' && camp) carregarProdutosFiltro();
    // eslint-disable-next-line
  }, [aba, camp?.classes?.length, camp?.fornecedores?.length]);

  const salvarConfig = async () => {
    if (form.data_inicio && form.data_fim && form.data_inicio > form.data_fim) {
      return Swal.fire('Datas inválidas', 'A data de início não pode ser maior que a data de fim.', 'warning');
    }
    setSalvando(true);
    try {
      // Referência sempre em dia/mês (ano 2000 como marcador). Sem personalização,
      // acompanha as datas da campanha — antes ela só era preenchida quando estava
      // vazia e ficava para trás quando as datas da campanha mudavam.
      const payload = { ...form };
      delete payload.ref_personalizada;
      payload.periodo_ref_inicio = refDeMD(form.ref_personalizada ? mdDe(form.periodo_ref_inicio) : mdDe(form.data_inicio)) || null;
      payload.periodo_ref_fim = refDeMD(form.ref_personalizada ? mdDe(form.periodo_ref_fim) : mdDe(form.data_fim)) || null;
      await axios.put(`${API}/campanhas-vendedor/${id}`, payload);
      if (form.tipo === 'ranking') {
        const lista = premios.slice(0, parseInt(form.qtd_ganhadores) || 3).map((p, i) => ({ posicao: i + 1, valor: parseFloat(p.valor) || 0 }));
        await axios.put(`${API}/campanhas-vendedor/${id}/premios`, { premios: lista });
      }
      Swal.fire({ icon: 'success', title: 'Salvo!', timer: 1200, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao salvar', 'error');
    } finally { setSalvando(false); }
  };

  // Ajusta a lista de prêmios ao mudar quantos ganham
  const setQtdGanhadores = (n) => {
    const q = Math.max(1, Math.min(20, parseInt(n) || 1));
    setForm(f => ({ ...f, qtd_ganhadores: q }));
    setPremios(prev => Array.from({ length: q }, (_, i) => prev[i] || { posicao: i + 1, valor: '' }));
  };

  // ---- Classes ----
  const addClasse = async (cls) => {
    if (camp.classes.find(c => c.clasprod === cls.clasprod)) return;
    try {
      await axios.post(`${API}/campanhas-vendedor/${id}/classes`, { classes: [{ clasprod: cls.clasprod, classe_nome: cls.descricao }] });
      // NÃO fecha dropdown nem limpa input — usuário pode continuar adicionando
      carregar();
    } catch (err) { Swal.fire('Erro', 'Erro ao adicionar classe', 'error'); }
  };

  const removerClasse = async (classeId) => {
    await axios.delete(`${API}/campanhas-vendedor/${id}/classes/${classeId}`);
    carregar();
  };

  // ---- Fornecedores ----
  const [fornecedoresBusca, setFornecedoresBusca] = useState([]);
  const [fornInput, setFornInput] = useState('');
  const [fornDropOpen, setFornDropOpen] = useState(false);

  useEffect(() => {
    if (!fornDropOpen) return;
    const t = setTimeout(() => {
      axios.get(`${API}/produtos/fornecedores`, { params: fornInput ? { q: fornInput } : {} })
        .then(r => setFornecedoresBusca(r.data))
        .catch(() => setFornecedoresBusca([]));
    }, 250);
    return () => clearTimeout(t);
  }, [fornInput, fornDropOpen]);

  const addFornecedor = async (f) => {
    if (camp.fornecedores?.find(x => x.fornecedor_oid === f.fornecedor_oid)) return;
    try {
      await axios.post(`${API}/campanhas-vendedor/${id}/fornecedores`, {
        fornecedores: [{ fornecedor_oid: f.fornecedor_oid, razsoc: f.razsoc, nome: f.nome, cnpj: f.cnpj }]
      });
      // NÃO fecha dropdown — continua aberto pra adicionar mais
      carregar();
    } catch (err) { Swal.fire('Erro', 'Erro ao adicionar fornecedor', 'error'); }
  };

  const removerFornecedor = async (fornecedorId) => {
    await axios.delete(`${API}/campanhas-vendedor/${id}/fornecedores/${fornecedorId}`);
    carregar();
  };

  const fornecedoresFiltrados = fornecedoresBusca.filter(f => !camp?.fornecedores?.find(x => x.fornecedor_oid === f.fornecedor_oid));

  const classesFiltradas = classesBDenter.filter(c =>
    (c.clasprod.toLowerCase().includes(classeInput.toLowerCase()) ||
    c.descricao.toLowerCase().includes(classeInput.toLowerCase())) &&
    !camp?.classes.find(x => x.clasprod === c.clasprod)
  ).slice(0, 20);

  // ---- Filiais ----
  // A chave do grupo e sempre os codigos ordenados ('06+08'). Recanonizar a cada
  // mudanca evita chave orfa — desmarcar a 08 tem que deixar a 06 como '06', nao
  // '06+08'. O backend refaz o mesmo calculo ao salvar.
  const canonizarGrupos = (lista) => {
    const porGrupo = new Map();
    lista.forEach(f => {
      if (!porGrupo.has(f.grupo)) porGrupo.set(f.grupo, []);
      porGrupo.get(f.grupo).push(f.filial_codigo);
    });
    const canon = new Map([...porGrupo.entries()].map(([g, cods]) => [
      g, [...cods].sort((a, b) => a - b).map(c => String(c).padStart(2, '0')).join('+'),
    ]));
    return lista.map(f => ({ ...f, grupo: canon.get(f.grupo) }));
  };

  const toggleFilial = (codigo) => {
    setFiliaisConfig(prev => {
      const semEla = prev.filter(f => f.filial_codigo !== codigo);
      if (prev.length !== semEla.length) return canonizarGrupos(semEla);
      return canonizarGrupos([...prev, { filial_codigo: codigo, grupo: String(codigo).padStart(2, '0') }]);
    });
  };

  const salvarFiliais = async () => {
    try {
      await axios.post(`${API}/campanhas-vendedor/${id}/filiais`, { filiais: filiaisConfig });
      Swal.fire({ icon: 'success', title: 'Filiais salvas!', timer: 1200, showConfirmButton: false });
      carregar();
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error'); }
  };

  // ---- Metas ----
  const isMetaManual = (m) => {
    const calc = parseFloat(m.meta_calculada);
    const final = parseFloat(m.meta_final);
    return !isNaN(calc) && !isNaN(final) && Math.abs(calc - final) > 0.01;
  };

  const recalcularTudo = async () => {
    const anos = parseInt(form.qtd_anos_ref) || 1;
    const tipo = form.tipo || 'vendedor';
    const isG = tipo === 'gerente';
    const subject = isG ? 'o <b>total da loja</b>' : 'o <b>TOP vendedor</b> de cada grupo';
    const msgAnos = anos > 1
      ? `Vou buscar ${subject} em <b>cada um dos últimos ${anos} anos</b> (mesmo período mês/dia), fazer a <b>média</b> dos valores e aplicar `
      : `Vou buscar as vendas do período de referência, identificar ${subject} e aplicar `;

    const manuais = (camp.metas || []).filter(isMetaManual);
    const aviso = manuais.length > 0
      ? `<div class="alert alert-warning mt-3 mb-0" style="font-size:0.85rem;text-align:left">
           <i class="bi bi-exclamation-triangle me-1"></i>
           <b>${manuais.length} meta${manuais.length > 1 ? 's' : ''} editada${manuais.length > 1 ? 's' : ''} manualmente</b> ${manuais.length > 1 ? 'serão sobrescritas' : 'será sobrescrita'}: ${manuais.map(m => m.grupo).join(', ')}.
         </div>` : '';

    const r = await Swal.fire({
      title: 'Recalcular histórico e metas?',
      html: msgAnos + '<b>' + form.percentual_meta + '%</b> sobre o valor.' +
        '<br><br><small class="text-muted">Pode demorar alguns segundos.</small>' + aviso,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Recalcular', cancelButtonText: 'Cancelar', confirmButtonColor: '#6B2C5C',
    });
    if (!r.isConfirmed) return;

    try {
      Swal.fire({ title: 'Recalculando...', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
      const res = await axios.post(`${API}/campanhas-vendedor/${id}/calcular-metas`);
      // Recarrega tudo: campanha (metas atualizadas) + histórico (anos detalhados)
      const histRes = await axios.get(`${API}/campanhas-vendedor/${id}/historico`);
      setHistorico(histRes.data);
      Swal.fire({ icon: 'success', title: `${res.data.metas.length} metas recalculadas!`, timer: 1500, showConfirmButton: false });
      carregar();
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao recalcular', 'error'); }
  };

  const editarMeta = async (meta) => {
    const { value } = await Swal.fire({
      title: `Meta do grupo ${meta.grupo}`,
      html: `
        <input id="sw-meta" class="swal2-input" type="number" step="0.01" value="${meta.meta_final || meta.meta_calculada}">
        <div class="alert alert-info mt-3 mb-0" style="font-size:0.82rem;text-align:left">
          <i class="bi bi-info-circle me-1"></i>
          A meta ficará marcada como <b>manual</b>. Ao recalcular metas, ela será sobrescrita.
        </div>
      `,
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar', confirmButtonColor: '#6B2C5C',
      preConfirm: () => document.getElementById('sw-meta').value,
    });
    if (!value) return;
    await axios.put(`${API}/campanhas-vendedor/${id}/metas/${meta.id}`, { meta_final: parseFloat(value) });
    carregar();
  };

  const restaurarMeta = async (meta) => {
    const r = await Swal.fire({
      title: 'Restaurar meta calculada?',
      html: `Vai voltar para <b>R$ ${fmt(meta.meta_calculada)}</b> (valor original do cálculo).`,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Restaurar', cancelButtonText: 'Cancelar', confirmButtonColor: '#6B2C5C',
    });
    if (!r.isConfirmed) return;
    await axios.put(`${API}/campanhas-vendedor/${id}/metas/${meta.id}`, { meta_final: parseFloat(meta.meta_calculada) });
    carregar();
  };

  // ---- Desempate ----
  const moverCriterio = async (idx, delta) => {
    const novos = [...camp.desempate];
    const novaPos = idx + delta;
    if (novaPos < 0 || novaPos >= novos.length) return;
    [novos[idx], novos[novaPos]] = [novos[novaPos], novos[idx]];
    const criterios = novos.map((c, i) => ({ criterio: c.criterio, ordem: i + 1 }));
    await axios.post(`${API}/campanhas-vendedor/${id}/desempate`, { criterios });
    carregar();
  };

  // ---- Status ----
  const mudarStatus = async (novoStatus) => {
    const r = await Swal.fire({ title: `Mudar para "${novoStatus}"?`, icon: 'question', showCancelButton: true, confirmButtonColor: '#6B2C5C' });
    if (!r.isConfirmed) return;
    await axios.put(`${API}/campanhas-vendedor/${id}`, { status: novoStatus });
    carregar();
  };

  // ---- Visibilidade: gerente e vendedor sao independentes ----
  // Campanha de premio de gerente, por exemplo, e liberada pro gerente e fica
  // oculta pro vendedor — antes um interruptor so controlava os dois.
  const toggleVisibilidade = async (campo) => {
    const novo = !camp[campo];
    const quem = campo === 'visivel_gerente' ? 'gerentes' : 'vendedores';
    try {
      await axios.put(`${API}/campanhas-vendedor/${id}`, { [campo]: novo });
      Swal.fire({ icon: 'success', title: novo ? `Liberada pros ${quem}` : `Oculta dos ${quem}`, timer: 1300, showConfirmButton: false });
      carregar();
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro ao alterar visibilidade', 'error'); }
  };

  // ---- Resultado ----
  const carregarResultado = async () => {
    try {
      Swal.fire({ title: 'Buscando resultado...', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
      const res = await axios.get(`${API}/campanhas-vendedor/${id}/resultado`);
      setResultado(res.data);
      Swal.close();
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error'); }
  };

  const abrirDetalheVendedor = async (codvend, nome, grupo) => {
    setDetalheVendedor({ codvend, nome, grupo, dados: null });
    setLoadingDetalhe(true);
    try {
      const res = await axios.get(`${API}/campanhas-vendedor/${id}/vendedor/${encodeURIComponent(codvend)}/detalhe`);
      setDetalheVendedor({ codvend, nome, grupo, dados: res.data });
    } catch (err) {
      setDetalheVendedor(null);
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao buscar detalhe', 'error');
    } finally { setLoadingDetalhe(false); }
  };

  const carregarHistorico = async () => {
    try {
      Swal.fire({ title: 'Buscando histórico...', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
      const res = await axios.get(`${API}/campanhas-vendedor/${id}/historico`);
      setHistorico(res.data);
      Swal.close();
    } catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error'); }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>;
  if (!camp) return <div className="text-center py-5 text-muted">Campanha não encontrada</div>;

  const isRascunho = camp.status === 'rascunho';
  const isGerente = camp.tipo === 'gerente';
  const isQuantidade = camp.tipo === 'quantidade';
  const isRanking = camp.tipo === 'ranking';
  const unidadePremiada = (camp.unidade_premiada || 'M2');

  // ---- Exportar resultado em CSV (pro RH lançar a premiação) ----
  const exportarResultado = () => {
    if (!resultado?.resultado?.length) {
      Swal.fire('Nada pra exportar', 'Carregue o resultado antes de exportar.', 'warning');
      return;
    }
    const num = v => parseFloat(v || 0).toFixed(2).replace('.', ',');  // Excel pt-BR lê como número
    const txt = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const unid = (resultado.unidade_premiada || 'un').toLowerCase();
    const linhas = [];
    let head = [];
    let totalPremio = 0;

    if (isQuantidade) {
      head = ['Filial', 'Cód. Vendedor', 'Vendedor', 'Posição', `Vendido (${unid})`, `Devolvido (${unid})`, `Líquido (${unid})`, 'Prêmio (R$)'];
      resultado.resultado.forEach(g => g.vendedores.forEach(v => {
        totalPremio += parseFloat(v.premio) || 0;
        linhas.push([txt(nomeGrupo(g.grupo)), txt(v.codvend), txt(v.nome), v.posicao,
          num(v.quantBruta), num(v.devolQuant), num(v.quant), num(v.premio)]);
      }));
    } else if (isRanking) {
      const porQtd = resultado.rankear_por === 'quantidade';
      head = ['Filial', 'Cód. Vendedor', 'Vendedor', 'Posição', porQtd ? 'Quantidade' : 'Venda líquida (R$)', 'Ganhador', 'Prêmio (R$)'];
      resultado.resultado.forEach(g => g.vendedores.forEach(v => {
        totalPremio += parseFloat(v.premio) || 0;
        linhas.push([txt(nomeGrupo(g.grupo)), txt(v.codvend), txt(v.nome), v.posicao,
          num(v.valor), txt(v.ganhador ? 'SIM' : 'NÃO'), num(v.premio)]);
      }));
    } else if (isGerente) {
      head = ['Filial', 'Meta (R$)', 'Venda líquida (R$)', '% da meta', 'Atingiu'];
      resultado.resultado.forEach(g => linhas.push([txt(nomeGrupo(g.grupo)), num(g.meta),
        num(g.total?.vendaLiquida), num(g.total?.percentualMeta), txt(g.total?.atingiu ? 'SIM' : 'NÃO')]));
    } else {
      head = ['Filial', 'Cód. Vendedor', 'Vendedor', 'Posição', 'Venda líquida (R$)', 'Meta (R$)', '% da meta', 'Atingiu'];
      resultado.resultado.forEach(g => g.vendedores.forEach(v => linhas.push([
        txt(nomeGrupo(g.grupo)), txt(v.codvend), txt(v.nome), v.posicao,
        num(v.vendaLiquida), num(g.meta), num(v.percentualMeta), txt(v.atingiu ? 'SIM' : 'NÃO')])));
    }

    const corpo = [
      [txt(resultado.campanha), txt(`Período: ${resultado.periodo || ''}`)].join(';'),
      '',
      head.map(txt).join(';'),
      ...linhas.map(l => l.join(';')),
      ...((isQuantidade || isRanking) ? ['', [txt('TOTAL GERAL'), ...Array(head.length - 2).fill(''), num(totalPremio)].join(';')] : []),
    ].join('\r\n');

    const slug = String(resultado.campanha || 'campanha').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
    const hoje = new Date().toISOString().slice(0, 10);
    const blob = new Blob(['\uFEFF' + corpo], { type: 'text/csv;charset=utf-8;' });  // BOM = acento certo no Excel
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `premiacao-${slug}-${hoje}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => navigate('/campanhas-vendedor')}>
            <i className="bi bi-arrow-left"></i>
          </button>
          <span className="fw-bold fs-5">{camp.nome}</span>
          <span className="ms-2 badge" style={{ background: isGerente ? '#4A1F40' : isQuantidade ? '#1F5C4A' : isRanking ? '#8a6d00' : '#6B2C5C', color: '#fff', fontSize: '0.75rem' }}>
            <i className={`bi ${isGerente ? 'bi-person-badge' : isQuantidade ? 'bi-rulers' : isRanking ? 'bi-trophy' : 'bi-person'} me-1`}></i>
            {isGerente ? 'Gerente' : isQuantidade ? `Quantidade (R$/${unidadePremiada})` : isRanking ? 'Top vendedores' : 'Vendedor'}
          </span>
          <span className={`ms-2 badge-${camp.status}`}>{camp.status}</span>
          {camp.fornecedor && <span className="ms-2 text-muted" style={{ fontSize: '0.9rem' }}>🏢 {camp.fornecedor}</span>}
        </div>
        <div className="d-flex gap-2">
          {podeEditar && isRascunho && <button className="btn btn-sm btn-success" onClick={() => mudarStatus('ativa')}><i className="bi bi-play-fill me-1"></i>Ativar</button>}
          {podeEditar && camp.status === 'ativa' && <>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => mudarStatus('rascunho')}>Voltar Rascunho</button>
            <button className="btn btn-sm btn-secondary" onClick={() => mudarStatus('encerrada')}>Encerrar</button>
          </>}
          {podeEditar && camp.status === 'encerrada' && <button className="btn btn-sm btn-outline-success" onClick={() => mudarStatus('ativa')}>Reabrir</button>}
          {podeEditar && (
            <>
              <button
                className={`btn btn-sm ${camp.visivel_gerente ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                onClick={() => toggleVisibilidade('visivel_gerente')}
                title={camp.visivel_gerente ? 'Os gerentes de loja enxergam esta campanha. Clique para ocultar.' : 'Esta campanha está oculta dos gerentes de loja. Clique para liberar.'}>
                <i className={`bi ${camp.visivel_gerente ? 'bi-eye' : 'bi-eye-slash'} me-1`}></i>
                {camp.visivel_gerente ? 'Gerentes veem' : 'Oculta p/ gerentes'}
              </button>
              <button
                className={`btn btn-sm ${camp.visivel_vendedor ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                onClick={() => toggleVisibilidade('visivel_vendedor')}
                title={camp.visivel_vendedor
                  ? 'Os vendedores enxergam esta campanha no Relatório de Evolução (Portal Comercial). Clique para ocultar.'
                  : 'Esta campanha está oculta dos vendedores. Clique para liberar.'}>
                <i className={`bi ${camp.visivel_vendedor ? 'bi-eye' : 'bi-eye-slash'} me-1`}></i>
                {camp.visivel_vendedor ? 'Vendedores veem' : 'Oculta p/ vendedores'}
              </button>
            </>
          )}
          <button className="btn btn-sm btn-outline-success" onClick={() => baixarApresentacao('pdf')}
            title="PDF completo, com metas e valores por loja — pra enviar ao fornecedor">
            <i className="bi bi-file-earmark-richtext me-1"></i>Apresentação (PDF)
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={() => baixarApresentacao('imagem')}
            title="Imagem de divulgação, mesmo layout sem valores de venda nem meta — pra mandar aos vendedores">
            <i className="bi bi-image me-1"></i>Imagem (sem metas)
          </button>
          {regrasPdf?.existe && (
            <button className="btn btn-sm btn-outline-dark" onClick={baixarRegrasPdf}>
              <i className="bi bi-file-pdf me-1"></i>Regras (PDF)
            </button>
          )}
        </div>
      </div>

      {/* Abas */}
      <ul className="nav nav-tabs mb-3">
        {[
          { k: 'resultado', label: 'Dashboard', icon: 'bi-speedometer2' },
          ...(isQuantidade || isRanking ? [] : [{ k: 'metas', label: 'Metas', icon: 'bi-bullseye' }]),
          { k: 'config', label: 'Configurações', icon: 'bi-gear' },
          { k: 'classes', label: 'Classes / Fornecedores', icon: 'bi-tags' },
          { k: 'filiais', label: 'Filiais', icon: 'bi-shop' },
          ...(isGerente || isQuantidade || isRanking || soLeitura ? [] : [{ k: 'desempate', label: 'Desempate', icon: 'bi-shuffle' }]),
        ].map(a => (
          <li className="nav-item" key={a.k}>
            <button className={`nav-link ${aba === a.k ? 'active' : ''}`} onClick={() => setAba(a.k)}>
              <i className={`bi ${a.icon} me-1`}></i>{a.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ABA CONFIG */}
      {aba === 'config' && (
        <div className="card p-3">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Nome</label>
              <input className="form-control" value={form.nome || ''} onChange={e => setForm({ ...form, nome: e.target.value })} disabled={!isRascunho || soLeitura} />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Fornecedor</label>
              <input className="form-control" value={form.fornecedor || ''} onChange={e => setForm({ ...form, fornecedor: e.target.value })} disabled={!isRascunho || soLeitura} />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Tipo</label>
              <select className="form-select" value={form.tipo || 'vendedor'} onChange={e => setForm({ ...form, tipo: e.target.value })} disabled={!isRascunho || soLeitura}>
                <option value="vendedor">Vendedor (top 1 de cada loja, com meta)</option>
                <option value="gerente">Gerente (total da loja, com meta)</option>
                <option value="quantidade">Por quantidade (R$ por unidade)</option>
                <option value="ranking">Top vendedores (ranking, sem meta)</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Data Início Campanha</label>
              <input type="date" className="form-control" value={form.data_inicio?.split('T')[0] || ''}
                onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))}
                disabled={!isRascunho || soLeitura} />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Data Fim Campanha</label>
              <input type="date" className="form-control" value={form.data_fim?.split('T')[0] || ''}
                onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))}
                disabled={!isRascunho || soLeitura} />
            </div>
            {(form.tipo === 'vendedor' || form.tipo === 'gerente') && (
              <>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Período de referência (histórico)</label>
                  <div className="form-check mb-1">
                    <input className="form-check-input" type="checkbox" id="ref-personalizada"
                      checked={!!form.ref_personalizada}
                      disabled={!isRascunho || soLeitura}
                      onChange={e => {
                        const on = e.target.checked;
                        setForm(f => ({
                          ...f,
                          ref_personalizada: on,
                          // Ao ligar, parte do período da campanha pra você ajustar.
                          ...(on && !refPersonalizada(f)
                            ? { periodo_ref_inicio: refDeMD(mdDe(f.data_inicio)), periodo_ref_fim: refDeMD(mdDe(f.data_fim)) }
                            : {}),
                        }));
                      }} />
                    <label className="form-check-label" htmlFor="ref-personalizada" style={{ fontSize: '0.8rem' }}>
                      Usar período diferente do da campanha
                    </label>
                  </div>
                  {form.ref_personalizada && (
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <SeletorDiaMes value={mdDe(form.periodo_ref_inicio)} disabled={!isRascunho || soLeitura}
                        onChange={md => setForm(f => ({ ...f, periodo_ref_inicio: refDeMD(md) }))} />
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>a</span>
                      <SeletorDiaMes value={mdDe(form.periodo_ref_fim)} disabled={!isRascunho || soLeitura}
                        onChange={md => setForm(f => ({ ...f, periodo_ref_fim: refDeMD(md) }))} />
                    </div>
                  )}
                  <small className="text-muted d-block">
                    <i className="bi bi-calendar-range me-1"></i>{resumoReferencia(form)}
                  </small>
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Anos (média)</label>
                  <select className="form-select" value={form.qtd_anos_ref || 1} onChange={e => setForm({ ...form, qtd_anos_ref: parseInt(e.target.value) })} disabled={!isRascunho || soLeitura}>
                    <option value="1">1 ano</option>
                    <option value="2">2 anos</option>
                    <option value="3">3 anos</option>
                    <option value="4">4 anos</option>
                    <option value="5">5 anos</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>% Meta</label>
                  <input type="number" step="0.1" className="form-control" value={form.percentual_meta || ''} onChange={e => setForm({ ...form, percentual_meta: e.target.value })} disabled={soLeitura} />
                </div>
              </>
            )}
            {form.tipo === 'quantidade' && (
              <>
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>R$ por unidade</label>
                  <input type="number" step="0.01" className="form-control" placeholder="Ex: 1,00"
                    value={form.valor_por_unidade ?? ''} onChange={e => setForm({ ...form, valor_por_unidade: e.target.value })} disabled={!isRascunho || soLeitura} />
                </div>
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Unidade premiada</label>
                  <select className="form-select" value={form.unidade_premiada || 'M2'}
                    onChange={e => setForm({ ...form, unidade_premiada: e.target.value })} disabled={!isRascunho || soLeitura}>
                    {!unidades.some(u => u.unidade === (form.unidade_premiada || 'M2')) && (form.unidade_premiada || 'M2') && (
                      <option value={form.unidade_premiada || 'M2'}>{form.unidade_premiada || 'M2'}</option>
                    )}
                    {unidades.map(u => <option key={u.unidade} value={u.unidade}>{u.unidade} ({u.qtd})</option>)}
                  </select>
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <small className="text-muted"><i className="bi bi-info-circle me-1"></i>Prêmio = quantidade líquida vendida (na unidade premiada) × R$ por unidade. Sem meta.</small>
                </div>
              </>
            )}
            {form.tipo === 'ranking' && (
              <>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Rankear por</label>
                  <div className="d-flex gap-3 mt-1">
                    <label style={{ fontSize: '0.88rem' }}><input type="radio" className="me-1" checked={form.rankear_por === 'valor'} onChange={() => setForm({ ...form, rankear_por: 'valor' })} disabled={!isRascunho || soLeitura} />Valor (R$)</label>
                    <label style={{ fontSize: '0.88rem' }}><input type="radio" className="me-1" checked={form.rankear_por === 'quantidade'} onChange={() => setForm({ ...form, rankear_por: 'quantidade' })} disabled={!isRascunho || soLeitura} />Quantidade</label>
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Quantos ganham</label>
                  <input type="number" min="1" max="20" className="form-control" value={form.qtd_ganhadores || 3} onChange={e => setQtdGanhadores(e.target.value)} disabled={soLeitura} />
                </div>
                <div className="col-12">
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Prêmio por posição (R$)</label>
                  <div className="d-flex flex-wrap gap-2">
                    {premios.map((p, i) => (
                      <div key={i} className="d-flex align-items-center gap-1" style={{ background: '#f7f2f6', border: '1px solid #e6d8e2', borderRadius: 8, padding: '4px 8px' }}>
                        <span className="fw-bold" style={{ color: '#6B2C5C', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{i + 1}º</span>
                        <div className="input-group input-group-sm" style={{ width: 130 }}>
                          <span className="input-group-text">R$</span>
                          <input type="number" step="0.01" className="form-control" value={p.valor} onChange={e => setPremios(prev => prev.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))} disabled={soLeitura} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted d-block mt-1"><i className="bi bi-info-circle me-1"></i>Os <b>{form.qtd_ganhadores || 3}</b> melhores vendedores de cada loja no período ganham. Sem meta, sem comparação — só quem vendeu mais desses produtos.</small>
                </div>
              </>
            )}
            {(form.tipo === 'vendedor' || form.tipo === 'gerente') && (
              <div className="col-md-9">
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Premiação <small className="text-muted">(opcional)</small></label>
                <input className="form-control" placeholder="Ex: R$ 1.000 por loja + iPad pra top 1" value={form.premiacao || ''} onChange={e => setForm({ ...form, premiacao: e.target.value })} disabled={soLeitura} />
              </div>
            )}
            <div className="col-12">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Observações</label>
              <textarea className="form-control" rows="3" value={form.observacoes || ''} onChange={e => setForm({ ...form, observacoes: e.target.value })} disabled={soLeitura} />
            </div>
          </div>

          {/* Regras da campanha (PDF) — upload (so quem edita) */}
          {podeEditar && (
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid #eee' }}>
              <h6 className="fw-bold mb-1"><i className="bi bi-file-earmark-pdf me-1" style={{ color: '#6B2C5C' }}></i>Regras da campanha (PDF)</h6>
              <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>
                Suba o PDF com o regulamento/regras desta campanha. É ele que abre no botão <strong>Regras (PDF)</strong> lá em cima — pra você e pros gerentes de loja.
              </p>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {regrasPdf?.existe
                  ? <button className="btn btn-sm btn-outline-dark" onClick={baixarRegrasPdf}><i className="bi bi-eye me-1"></i>Ver atual</button>
                  : <span className="badge bg-secondary">Nenhum PDF enviado</span>}
                <label className="btn btn-sm btn-primary-mkt mb-0" style={{ cursor: 'pointer' }}>
                  <i className="bi bi-upload me-1"></i>{subindoPdf ? 'Enviando...' : (regrasPdf?.existe ? 'Trocar PDF' : 'Subir PDF')}
                  <input type="file" accept="application/pdf" hidden onChange={subirRegrasPdf} disabled={subindoPdf} />
                </label>
                {regrasPdf?.existe && <button className="btn btn-sm btn-outline-danger" onClick={removerRegrasPdf}><i className="bi bi-trash me-1"></i>Remover</button>}
                {regrasPdf?.existe && regrasPdf.atualizado_em && <small className="text-muted">Atualizado em {new Date(regrasPdf.atualizado_em).toLocaleString('pt-BR', { timeZone: 'UTC' })}</small>}
              </div>
            </div>
          )}

          {!soLeitura && (
            <div className="mt-3">
              <button className="btn-salvar" onClick={salvarConfig} disabled={salvando}>
                {salvando ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</> : <><i className="bi bi-check-lg me-1"></i>Salvar</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ABA CLASSES + FORNECEDORES */}
      {aba === 'classes' && (
        <div className="row g-3">
          {/* CLASSES */}
          <div className="col-md-6">
            <div className="card p-3 h-100">
              <h6 className="fw-bold mb-2"><i className="bi bi-tags me-1"></i>Classes</h6>
              <small className="text-muted mb-2 d-block">Produtos cuja classe começa com o código informado entram no cálculo.</small>
              {!soLeitura && (
              <div style={{ position: 'relative' }}>
                <input className="form-control" placeholder="Buscar classe (digite código ou descrição)..."
                  value={classeInput} onChange={e => { setClasseInput(e.target.value); setClasseDropOpen(true); }}
                  onFocus={() => setClasseDropOpen(true)} />
                {classeDropOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 300, overflowY: 'auto', background: '#fff', border: '1px solid #ddd', zIndex: 10, borderRadius: 4, marginTop: 2 }}>
                    <div style={{ padding: '4px 12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', fontSize: '0.75rem', color: '#666' }}>
                      <span>{classesFiltradas.length} classe(s) — clique para adicionar</span>
                      <button className="btn btn-sm p-0" onClick={() => setClasseDropOpen(false)} style={{ lineHeight: 1, color: '#666' }}><i className="bi bi-x-lg"></i></button>
                    </div>
                    {classesFiltradas.length === 0 && <div className="text-muted text-center p-2" style={{ fontSize: '0.85rem' }}>Sem resultados</div>}
                    {classesFiltradas.map(c => (
                      <div key={c.clasprod} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.85rem' }}
                        onClick={() => addClasse(c)}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f6f0'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <strong>{c.clasprod}</strong> — {c.descricao}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="mt-3 d-flex flex-wrap gap-2">
                {camp.classes.map(c => (
                  <span key={c.id} className="badge-filial d-flex align-items-center gap-1">
                    <strong>{c.clasprod}</strong> {c.classe_nome}
                    {isRascunho && !soLeitura && <button className="btn btn-sm p-0 ms-1" onClick={() => removerClasse(c.id)} style={{ lineHeight: 1 }}><i className="bi bi-x"></i></button>}
                  </span>
                ))}
                {camp.classes.length === 0 && <span className="text-muted small">Nenhuma classe adicionada</span>}
              </div>
            </div>
          </div>

          {/* FORNECEDORES */}
          <div className="col-md-6">
            <div className="card p-3 h-100">
              <h6 className="fw-bold mb-2"><i className="bi bi-building me-1"></i>Fornecedores</h6>
              <small className="text-muted mb-2 d-block">Todos os produtos do fornecedor entram no cálculo (independente da classe).</small>
              {!soLeitura && (
              <div style={{ position: 'relative' }}>
                <input className="form-control" placeholder="Buscar fornecedor (razão social, nome ou CNPJ)..."
                  value={fornInput} onChange={e => { setFornInput(e.target.value); setFornDropOpen(true); }}
                  onFocus={() => setFornDropOpen(true)} />
                {fornDropOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 300, overflowY: 'auto', background: '#fff', border: '1px solid #ddd', zIndex: 10, borderRadius: 4, marginTop: 2 }}>
                    <div style={{ padding: '4px 12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', fontSize: '0.75rem', color: '#666' }}>
                      <span>{fornecedoresFiltrados.length} fornecedor(es) — clique para adicionar</span>
                      <button className="btn btn-sm p-0" onClick={() => setFornDropOpen(false)} style={{ lineHeight: 1, color: '#666' }}><i className="bi bi-x-lg"></i></button>
                    </div>
                    {fornecedoresFiltrados.length === 0 && <div className="text-muted text-center p-2" style={{ fontSize: '0.85rem' }}>{fornInput ? 'Sem resultados' : 'Digite para buscar'}</div>}
                    {fornecedoresFiltrados.map(f => (
                      <div key={f.fornecedor_oid} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.82rem' }}
                        onClick={() => addFornecedor(f)}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f6f0'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <strong>{f.razsoc}</strong>
                        <span className="text-muted ms-2">{f.cnpj}</span>
                        <span className="ms-2 badge bg-secondary" style={{ fontSize: '0.65rem' }}>{f.qtd_produtos} prod.</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="mt-3 d-flex flex-wrap gap-2">
                {(camp.fornecedores || []).map(f => (
                  <span key={f.id} className="badge-filial d-flex align-items-center gap-1" title={f.cnpj}>
                    <i className="bi bi-building me-1"></i>
                    <strong>{f.razsoc}</strong>
                    <span className="text-muted small ms-1">{f.cnpj}</span>
                    {isRascunho && !soLeitura && <button className="btn btn-sm p-0 ms-1" onClick={() => removerFornecedor(f.id)} style={{ lineHeight: 1 }}><i className="bi bi-x"></i></button>}
                  </span>
                ))}
                {(!camp.fornecedores || camp.fornecedores.length === 0) && <span className="text-muted small">Nenhum fornecedor adicionado</span>}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="alert alert-info py-2" style={{ fontSize: '0.82rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              <strong>Como funciona o filtro:</strong> um produto entra no cálculo se a classe dele bate <strong>OU</strong> o fornecedor dele bate. Você pode usar só classes, só fornecedores, ou ambos.
            </div>
          </div>

          {/* Produtos resolvidos pelo filtro */}
          <div className="col-12">
            <div className="card p-3">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h6 className="fw-bold mb-0"><i className="bi bi-box-seam me-1"></i>Produtos que entraram pelo filtro</h6>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {produtosFiltro && (
                    <small className="text-muted">
                      <b style={{ color: '#1F5C4A' }}>{produtosFiltro.total - excluidosProd.size}</b> marcado(s) de {produtosFiltro.total}
                      {isQuantidade && produtosFiltro.excluidos > 0 && <> · <span style={{ color: '#b53d3d' }}>{produtosFiltro.excluidos} fora da unidade</span></>}
                    </small>
                  )}
                  {salvandoProd && <small className="text-muted"><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />salvando</small>}
                  {!soLeitura && produtosFiltro?.total > 0 && (
                    <>
                      <button className="btn btn-sm btn-outline-secondary" onClick={marcarTodosProd}>Marcar todos</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={desmarcarTodosProd}>Desmarcar todos</button>
                    </>
                  )}
                  <button className="btn btn-sm btn-outline-secondary" onClick={carregarProdutosFiltro} disabled={loadingProdutos} title="Atualizar lista">
                    <i className="bi bi-arrow-repeat"></i>
                  </button>
                </div>
              </div>

              {loadingProdutos && <div className="text-center py-3"><div className="spinner-border spinner-border-sm" style={{ color: '#6B2C5C' }} /></div>}

              {!loadingProdutos && produtosFiltro && produtosFiltro.total === 0 && (
                <div className="text-muted text-center py-3" style={{ fontSize: '0.85rem' }}>Nenhum produto — adicione classes ou fornecedores acima.</div>
              )}

              {!loadingProdutos && produtosFiltro && produtosFiltro.total > 0 && (() => {
                const sorted = [...produtosFiltro.produtos];
                if (ordProd.campo) {
                  sorted.sort((a, b) => {
                    let va, vb;
                    if (ordProd.campo === 'marcado') { va = excluidosProd.has(a.codpro) ? 0 : 1; vb = excluidosProd.has(b.codpro) ? 0 : 1; }
                    else { va = (a[ordProd.campo] ?? '').toString().toLowerCase(); vb = (b[ordProd.campo] ?? '').toString().toLowerCase(); }
                    if (va < vb) return ordProd.dir === 'asc' ? -1 : 1;
                    if (va > vb) return ordProd.dir === 'asc' ? 1 : -1;
                    return 0;
                  });
                }
                const q = buscaProd.trim().toLowerCase();
                const filtrados = q ? sorted.filter(p => `${p.codpro} ${p.descricao}`.toLowerCase().includes(q)) : sorted;
                const todosMarcados = excluidosProd.size === 0;
                return (
                  <>
                    {!soLeitura && (
                      <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                        <i className="bi bi-info-circle me-1"></i>Por padrão <b>todos</b> entram. Desmarque os que <b>não</b> quer (ou "Desmarcar todos" e marque só os desejados). Vazio = todos entram.
                      </div>
                    )}
                    <div className="mb-2" style={{ position: 'relative', maxWidth: 340 }}>
                      <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '0.8rem', pointerEvents: 'none' }}></i>
                      <input className="form-control form-control-sm" style={{ paddingLeft: 30 }} placeholder="Buscar código ou nome na tabela..." value={buscaProd} onChange={e => setBuscaProd(e.target.value)} />
                      {buscaProd && <i className="bi bi-x-circle-fill" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '0.8rem', cursor: 'pointer' }} onClick={() => setBuscaProd('')}></i>}
                    </div>
                    {isQuantidade && produtosFiltro.excluidos > 0 && (
                      <div className="alert alert-warning py-2" style={{ fontSize: '0.82rem' }}>
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        <strong>{produtosFiltro.excluidos} produto(s)</strong> não estão em {unidadePremiada} e <strong>não entram no prêmio</strong> (badge vermelho), mesmo se marcados.
                      </div>
                    )}
                    <div className="table-responsive" style={{ maxHeight: 420, overflowY: 'auto' }}>
                      <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                          <tr>
                            <th style={{ width: 34, cursor: 'pointer' }} onClick={() => toggleOrdProd('marcado')}>
                              {!soLeitura && <input type="checkbox" checked={todosMarcados} onClick={e => e.stopPropagation()} ref={el => { if (el) el.indeterminate = !todosMarcados && excluidosProd.size < produtosFiltro.total; }} onChange={e => e.target.checked ? marcarTodosProd() : desmarcarTodosProd()} title="Marcar/desmarcar todos" />}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => toggleOrdProd('codpro')}>Código{iconOrd('codpro')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => toggleOrdProd('descricao')}>Descrição{iconOrd('descricao')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => toggleOrdProd('fornecedor')}>Fornecedor{iconOrd('fornecedor')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => toggleOrdProd('clasprod')}>Classe{iconOrd('clasprod')}</th>
                            <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleOrdProd('unid1')}>Unid.{iconOrd('unid1')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtrados.map(p => {
                            const marcado = !excluidosProd.has(p.codpro);
                            return (
                              <tr key={p.codpro} style={{ opacity: marcado ? 1 : 0.5, background: !marcado ? '#f6f6f6' : undefined }}>
                                <td>{!soLeitura ? <input type="checkbox" checked={marcado} onChange={() => toggleProd(p.codpro)} /> : (marcado ? <i className="bi bi-check2 text-success" /> : '')}</td>
                                <td className="fw-semibold" style={{ textDecoration: marcado ? 'none' : 'line-through' }}>{p.codpro}</td>
                                <td style={{ textDecoration: marcado ? 'none' : 'line-through' }}>{p.descricao}</td>
                                <td className="text-muted">{p.fornecedor || '—'}</td>
                                <td className="text-muted">{p.clasprod} {p.classe_nome}</td>
                                <td style={{ textAlign: 'center' }}>
                                  {isQuantidade && !p.incluido
                                    ? <Tooltip texto={`Vendido em ${p.unid1 || '?'} — fora da unidade premiada (${unidadePremiada})`}><span className="badge" style={{ background: '#b53d3d', color: '#fff', fontSize: '0.68rem' }}>{p.unid1 || '—'}</span></Tooltip>
                                    : <span className="badge" style={{ background: '#1F5C4A', color: '#fff', fontSize: '0.68rem' }}>{p.unid1 || '—'}</span>}
                                </td>
                              </tr>
                            );
                          })}
                          {filtrados.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-2" style={{ fontSize: '0.82rem' }}>Nenhum produto com "{buscaProd}"</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ABA FILIAIS */}
      {aba === 'filiais' && (
        <div className="card p-3">
          <h6 className="fw-bold mb-3">Filiais Participantes + Agrupamento</h6>
          {soLeitura ? (
            <>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>Filiais que participam desta campanha (somente leitura).</p>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Código</th><th>Filial</th><th>Grupo</th></tr></thead>
                  <tbody>
                    {camp.filiais.map(f => (
                      <tr key={f.filial_codigo}>
                        <td>{String(f.filial_codigo).padStart(2, '0')}</td>
                        <td>{filiais.find(x => parseInt(x.codigo) === f.filial_codigo)?.nome || `Filial ${f.filial_codigo}`}</td>
                        <td><span className="badge-filial">{f.grupo}</span></td>
                      </tr>
                    ))}
                    {camp.filiais.length === 0 && <tr><td colSpan="3" className="text-center text-muted">Nenhuma filial</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                Marque as filiais que participam. Se duas contam juntas (ex: Carangola e Carangola Express),
                escolha <b>"Junto com…"</b> — elas passam a somar venda e meta como uma loja só.
              </p>
              {(() => {
                // Grupos derivados da configuracao atual. A chave e sempre os codigos
                // ordenados ('06+08'), montada aqui e no backend — assim nao existe
                // mais grupo digitado errado nem invertido.
                const chave = (cods) => [...cods].sort((a, b) => a - b).map(c => String(c).padStart(2, '0')).join('+');
                const grupos = new Map(); // grupo -> [codigos]
                filiaisConfig.forEach(c => {
                  if (!grupos.has(c.grupo)) grupos.set(c.grupo, []);
                  grupos.get(c.grupo).push(c.filial_codigo);
                });
                const nomeDe = (cod) => filiais.find(f => parseInt(f.codigo) === cod)?.nome || String(cod);
                const rotuloGrupo = (cods) => cods.map(nomeDe).join(' + ');

                // Move a filial para o grupo de outra (ou a deixa sozinha) e
                // recanoniza as chaves dos dois grupos afetados.
                const mover = (codigo, destino) => {
                  setFiliaisConfig(prev => {
                    const alvo = destino === '' ? [codigo] : [...(grupos.get(destino) || []), codigo];
                    const novoGrupo = chave(alvo);
                    const depois = prev.map(c => (c.filial_codigo === codigo ? { ...c, grupo: novoGrupo } : c));
                    // Recalcula a chave de todos os grupos (quem saiu pode ter esvaziado um)
                    const porGrupo = new Map();
                    depois.forEach(c => {
                      if (!porGrupo.has(c.grupo)) porGrupo.set(c.grupo, []);
                      porGrupo.get(c.grupo).push(c.filial_codigo);
                    });
                    const canon = new Map([...porGrupo.entries()].map(([g, cods]) => [g, chave(cods)]));
                    return depois.map(c => ({ ...c, grupo: canon.get(c.grupo) }));
                  });
                };

                return (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead><tr><th></th><th>Código</th><th>Filial</th><th>Conta junto com</th></tr></thead>
                      <tbody>
                        {filiais.map(f => {
                          const cod = parseInt(f.codigo);
                          const cfg = filiaisConfig.find(x => x.filial_codigo === cod);
                          // Grupos de OUTRAS filiais marcadas — os destinos possíveis.
                          const destinos = [...grupos.entries()].filter(([, cods]) => !cods.includes(cod));
                          const sozinha = cfg && (grupos.get(cfg.grupo) || []).length === 1;
                          return (
                            <tr key={f.codigo}>
                              <td><input type="checkbox" checked={!!cfg} onChange={() => toggleFilial(cod)} /></td>
                              <td>{String(f.codigo).padStart(2, '0')}</td>
                              <td>{f.nome}</td>
                              <td>
                                {cfg && (
                                  <div className="d-flex align-items-center gap-2">
                                    <select className="form-select form-select-sm" style={{ maxWidth: 260 }}
                                      value={sozinha ? '' : cfg.grupo}
                                      onChange={e => mover(cod, e.target.value)}>
                                      <option value="">Sozinha</option>
                                      {destinos.map(([g, cods]) => (
                                        <option key={g} value={g}>Junto com {rotuloGrupo(cods)}</option>
                                      ))}
                                    </select>
                                    {!sozinha && (
                                      <span className="badge" style={{ background: '#1F5C4A', color: '#fff', fontSize: '0.68rem' }}
                                        title="Estas filiais somam venda e meta como uma loja só">
                                        {rotuloGrupo(grupos.get(cfg.grupo) || [])}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              <button className="btn-salvar" onClick={salvarFiliais}><i className="bi bi-check-lg me-1"></i>Salvar Filiais</button>
            </>
          )}
        </div>
      )}

      {/* ABA METAS — temporal */}
      {aba === 'metas' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h6 className="fw-bold mb-0">Metas por Filial</h6>
              <small className="text-muted">{isGerente ? 'Total da loja em' : 'Campeão de'} cada ano, média e meta final (+{camp.percentual_meta}%)</small>
            </div>
            {!soLeitura && (
              <button className="btn-primary-mkt" onClick={recalcularTudo}>
                <i className="bi bi-arrow-clockwise me-1"></i>
                {camp.metas?.length > 0 ? 'Recalcular Histórico e Metas' : 'Calcular Metas'}
              </button>
            )}
          </div>

          {camp.periodos_referencia?.length > 0 && (() => {
            const multi = camp.qtd_anos_ref > 1;
            const per = camp.periodos_referencia;
            const dm = (iso) => iso.split('-').reverse().slice(0, 2).join('/');
            return (
              <div className="card p-3 mb-3" style={{ background: '#f5ebf2', border: '1px solid var(--mostarda-claro)' }}>
                <div className="d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: '0.85rem' }}>
                  <div>
                    <i className="bi bi-calendar-range me-1" style={{ color: 'var(--mostarda)' }}></i>
                    <strong>Período de referência:</strong>{' '}
                    {dm(per[0].inicio)} a {dm(per[0].fim)} de {listaAnos(per.map(p => p.ano))}
                  </div>
                  <div>
                    <i className="bi bi-clock-history me-1" style={{ color: 'var(--mostarda)' }}></i>
                    <strong>Anos analisados:</strong> {camp.qtd_anos_ref} {multi ? '(média)' : ''}
                  </div>
                  {camp.data_inicio && camp.data_fim && (
                    <div>
                      <i className="bi bi-megaphone me-1" style={{ color: 'var(--mostarda)' }}></i>
                      <strong>Campanha:</strong> {new Date(camp.data_inicio).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} a {new Date(camp.data_fim).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {camp.metas.length === 0 && (
            <div className="card p-4 text-center text-muted">{soLeitura ? 'Metas ainda não calculadas para esta campanha.' : 'Sem metas. Clique em "Calcular Metas" para gerar.'}</div>
          )}

          {camp.metas.length > 0 && (
            <div className="card p-3">
              <div className="table-responsive">
                <table className="table table-mkt table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Filial / Grupo</th>
                      {historico && Object.keys(historico).sort((a,b) => a-b).map(ano => <th key={ano} style={{ textAlign: 'center' }}>{ano}</th>)}
                      <th style={{ textAlign: 'center', background: '#8B4578' }}>Média / Ref.</th>
                      <th style={{ textAlign: 'center', background: '#4A1F40' }}>META {camp.percentual_meta}%</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {camp.metas.map(m => (
                      <tr key={m.id}>
                        <td>
                          <span className="badge-filial"><i className="bi bi-shop me-1"></i>{nomeGrupo(m.grupo)}</span>
                        </td>
                        {historico && Object.keys(historico).sort((a,b) => a-b).map(ano => {
                          const dados = historico[ano]?.[m.grupo];
                          return (
                            <td key={ano} style={{ textAlign: 'center', minWidth: 140 }}>
                              {dados ? (
                                <div>
                                  <div className="fw-bold">R$ {fmt(dados.venda_liquida)}</div>
                                  {!isGerente && (
                                    <span className="badge" style={{ background: 'var(--mostarda-claro)', color: 'var(--mostarda)', fontSize: '0.7rem', padding: '3px 8px', marginTop: 2 }}>
                                      {dados.top_vendedor}
                                    </span>
                                  )}
                                  {isGerente && (
                                    <small className="text-muted">Loja toda</small>
                                  )}
                                </div>
                              ) : <span className="text-muted">—</span>}
                            </td>
                          );
                        })}
                        <td style={{ textAlign: 'center', background: '#f5ebf2' }}>
                          <div className="fw-bold">R$ {fmt(m.valor_referencia)}</div>
                        </td>
                        <td style={{ textAlign: 'center', background: 'var(--mostarda-claro)' }}>
                          <div className="fw-bold" style={{ color: 'var(--mostarda)', fontSize: '1rem' }}>R$ {fmt(m.meta_final || m.meta_calculada)}</div>
                          {isMetaManual(m) && (
                            <Tooltip texto={`Editada manualmente. Valor calculado original: R$ ${fmt(m.meta_calculada)}`}>
                              <span className="badge mt-1" style={{ backgroundColor: '#FFA500', color: '#fff', fontSize: '0.65rem' }}>
                                <i className="bi bi-pencil-fill me-1"></i>manual
                              </span>
                            </Tooltip>
                          )}
                        </td>
                        <td>
                          {!soLeitura && (
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => editarMeta(m)} title="Editar meta"><i className="bi bi-pencil"></i></button>
                              {isMetaManual(m) && (
                                <button className="btn btn-sm btn-outline-warning" onClick={() => restaurarMeta(m)} title="Restaurar valor calculado"><i className="bi bi-arrow-counterclockwise"></i></button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ABA DESEMPATE */}
      {aba === 'desempate' && (
        <div className="card p-3">
          <h6 className="fw-bold mb-3">Critérios de Desempate (ordem)</h6>
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Se dois vendedores tiverem a mesma venda líquida, aplica estes critérios na ordem abaixo para decidir o vencedor.
          </p>
          <div style={{ maxWidth: 500 }}>
            {camp.desempate.map((c, idx) => (
              <div key={c.id} className="d-flex align-items-center gap-2 py-2 border-bottom">
                <span className="fw-bold" style={{ width: 30 }}>{c.ordem}.</span>
                <span className="flex-grow-1">{CRITERIOS_LABELS[c.criterio] || c.criterio}</span>
                <button className="btn btn-sm btn-outline-secondary" disabled={idx === 0} onClick={() => moverCriterio(idx, -1)}><i className="bi bi-arrow-up"></i></button>
                <button className="btn btn-sm btn-outline-secondary" disabled={idx === camp.desempate.length - 1} onClick={() => moverCriterio(idx, 1)}><i className="bi bi-arrow-down"></i></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ABA RESULTADO */}
      {aba === 'resultado' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">Resultado em Tempo Real</h6>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={carregarResultado}><i className="bi bi-arrow-repeat me-1"></i>Atualizar</button>
              <button className="btn btn-sm btn-outline-mkt" onClick={exportarResultado} disabled={!resultado}
                title="Baixa a planilha da premiação (abre no Excel)">
                <i className="bi bi-file-earmark-spreadsheet me-1"></i>Exportar
              </button>
            </div>
          </div>
          {!resultado && <div className="text-center text-muted py-4">Clique em atualizar para carregar</div>}

          {/* Dashboard GERENTE: card por grupo com total da loja */}
          {isGerente && resultado?.resultado.map(g => {
            const t = g.total || {};
            const atingiu = t.atingiu;
            return (
              <div key={g.grupo} className="card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="badge-filial" style={{ fontSize: '0.9rem', padding: '6px 14px' }}>
                      <i className="bi bi-shop me-1"></i>{nomeGrupo(g.grupo)}
                    </span>
                    {atingiu
                      ? <span className="badge bg-success" style={{ fontSize: '0.85rem' }}><i className="bi bi-check-circle me-1"></i>Meta Atingida</span>
                      : <span className="badge bg-secondary" style={{ fontSize: '0.85rem' }}>Em andamento</span>}
                  </div>
                  <div style={{ background: 'var(--mostarda-claro)', padding: '8px 16px', borderRadius: 8, border: '2px solid var(--mostarda)' }}>
                    <small style={{ color: '#666' }}>META</small>
                    <div className="fw-bold" style={{ color: 'var(--mostarda)', fontSize: '1.2rem', lineHeight: 1 }}>R$ {fmt(g.meta)}</div>
                  </div>
                </div>

                <div className="row g-3 mt-2">
                  <div className="col-md-4">
                    <small className="text-muted">Venda Atual da Loja</small>
                    <div className="fw-bold" style={{ fontSize: '1.8rem', color: atingiu ? '#28a745' : '#333', lineHeight: 1.1 }}>
                      R$ {fmt(t.vendaLiquida || 0)}
                    </div>
                    <small className="text-muted">Bruta R$ {fmt(t.venda || 0)} | Devol. R$ {fmt(t.devol || 0)}</small>
                  </div>
                  <div className="col-md-2">
                    <small className="text-muted">MKP</small>
                    <div className="fw-bold" style={{ fontSize: '1.1rem' }}>{((t.mkp || 0) * 100).toFixed(1)}%</div>
                  </div>
                  <div className="col-md-2">
                    <small className="text-muted">Atendimentos</small>
                    <div className="fw-bold" style={{ fontSize: '1.1rem' }}>{t.atend || 0}</div>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted">% da meta</small>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <div style={{ flex: 1, background: '#eee', borderRadius: 6, height: 14 }}>
                        <div style={{ width: Math.min(100, t.percentualMeta || 0) + '%', background: atingiu ? '#28a745' : 'var(--mostarda)', height: '100%', borderRadius: 6, transition: 'width 0.3s' }} />
                      </div>
                      <strong style={{ minWidth: 60, textAlign: 'right' }}>{(t.percentualMeta || 0).toFixed(1)}%</strong>
                    </div>
                    {!atingiu && t.vendaLiquida < g.meta && (
                      <small className="text-muted">Falta R$ {fmt(g.meta - t.vendaLiquida)}</small>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Dashboard QUANTIDADE: prêmio por vendedor (R$ por unidade vendida) */}
          {isQuantidade && resultado?.resultado.map(g => (
            <div key={g.grupo} className="card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <span className="badge-filial" style={{ fontSize: '0.9rem', padding: '6px 14px' }}>
                  <i className="bi bi-shop me-1"></i>{nomeGrupo(g.grupo)}
                </span>
                <div className="d-flex gap-2 flex-wrap">
                  <div style={{ background: '#e9f3ef', padding: '6px 14px', borderRadius: 8, border: '1px solid #1F5C4A' }}>
                    <small style={{ color: '#666' }}>TOTAL {(resultado.unidade_premiada || 'UN')}</small>
                    <div className="fw-bold" style={{ color: '#1F5C4A', fontSize: '1.1rem', lineHeight: 1 }}>
                      {fmt(g.total_quant)} {(resultado.unidade_premiada || '').toLowerCase()}
                    </div>
                  </div>
                  <div style={{ background: 'var(--mostarda-claro)', padding: '6px 14px', borderRadius: 8, border: '2px solid var(--mostarda)' }}>
                    <small style={{ color: '#666' }}>PRÊMIO TOTAL</small>
                    <div className="fw-bold" style={{ color: 'var(--mostarda)', fontSize: '1.2rem', lineHeight: 1 }}>R$ {fmt(g.total_premio)}</div>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>#</th><th>Vendedor</th>
                      <th style={{ textAlign: 'right' }}>Vendido</th>
                      <th style={{ textAlign: 'right' }}>Devol.</th>
                      <th style={{ textAlign: 'right' }}>Líquido ({(resultado.unidade_premiada || 'un').toLowerCase()})</th>
                      <th style={{ textAlign: 'right' }}>Prêmio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(expandirGrupo[g.grupo] ? g.vendedores : g.vendedores.slice(0, 5)).map(v => (
                      <tr key={v.codvend} style={{ background: v.posicao === 1 ? '#e8f5ee' : undefined, cursor: 'pointer' }}
                        onClick={() => abrirDetalheVendedor(v.codvend, v.nome, nomeGrupo(g.grupo))}>
                        <td className="fw-bold">{v.posicao}º</td>
                        <td><span style={{ textDecoration: 'underline dotted' }}>{v.nome}</span> <i className="bi bi-search ms-1" style={{ fontSize: '0.7rem', color: '#1F5C4A' }}></i></td>
                        <td style={{ textAlign: 'right' }}>{fmt(v.quantBruta)}</td>
                        <td style={{ textAlign: 'right', color: v.devolQuant > 0 ? '#b53d3d' : '#999' }}>{v.devolQuant > 0 ? `−${fmt(v.devolQuant)}` : '—'}</td>
                        <td style={{ textAlign: 'right' }} className="fw-bold">{fmt(v.quant)}</td>
                        <td style={{ textAlign: 'right' }} className="fw-bold" >R$ {fmt(v.premio)}</td>
                      </tr>
                    ))}
                    {g.vendedores.length === 0 && <tr><td colSpan="6" className="text-center text-muted">Sem vendas no período</td></tr>}
                  </tbody>
                </table>
              </div>
              {g.vendedores.length > 5 && (
                <div className="text-center mt-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandirGrupo(prev => ({ ...prev, [g.grupo]: !prev[g.grupo] }))}>
                    {expandirGrupo[g.grupo]
                      ? <><i className="bi bi-chevron-up me-1"></i>Mostrar só top 5</>
                      : <><i className="bi bi-chevron-down me-1"></i>Ver todos ({g.vendedores.length} vendedores)</>}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Dashboard VENDEDOR: ranking top 5 por grupo */}
          {!isGerente && !isQuantidade && !isRanking && resultado?.resultado.map(g => (
            <div key={g.grupo} className="card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge-filial" style={{ fontSize: '0.9rem', padding: '6px 14px' }}>
                    <i className="bi bi-shop me-1"></i>{nomeGrupo(g.grupo)}
                  </span>
                </div>
                <div style={{ background: 'var(--mostarda-claro)', padding: '8px 16px', borderRadius: 8, border: '2px solid var(--mostarda)' }}>
                  <small style={{ color: '#666' }}>META</small>
                  <div className="fw-bold" style={{ color: 'var(--mostarda)', fontSize: '1.2rem', lineHeight: 1 }}>R$ {fmt(g.meta)}</div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead><tr><th>#</th><th>Vendedor</th><th>Venda Bruta</th><th>Devol.</th><th>Venda Líquida</th><th>MKP</th><th>Atend.</th><th>% Meta</th><th></th></tr></thead>
                  <tbody>
                    {(expandirGrupo[g.grupo] ? g.vendedores : g.vendedores.slice(0, 5)).map(v => (
                      <tr key={v.codvend} style={{ background: v.atingiu && v.posicao === 1 ? '#e8f5ee' : undefined, cursor: 'pointer' }}
                        onClick={() => abrirDetalheVendedor(v.codvend, v.nome, nomeGrupo(g.grupo))}>
                        <td className="fw-bold">{v.posicao}º</td>
                        <td><span style={{ textDecoration: 'underline dotted' }}>{v.nome}</span> <i className="bi bi-search ms-1" style={{ fontSize: '0.7rem', color: '#6B2C5C' }}></i></td>
                        <td>R$ {fmt(v.venda)}</td>
                        <td>R$ {fmt(v.devol)}</td>
                        <td className="fw-bold">R$ {fmt(v.vendaLiquida)}</td>
                        <td>{(v.mkp * 100).toFixed(1)}%</td>
                        <td>{v.atend}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div style={{ flex: 1, background: '#eee', borderRadius: 4, height: 8, minWidth: 80 }}>
                              <div style={{ width: Math.min(100, v.percentualMeta) + '%', background: v.atingiu ? '#28a745' : '#6B2C5C', height: '100%', borderRadius: 4 }} />
                            </div>
                            <small>{v.percentualMeta.toFixed(1)}%</small>
                          </div>
                        </td>
                        <td>
                          {v.atingiu ? <span className="badge bg-success">✓ Atingiu</span> : <span className="badge bg-secondary">Em andamento</span>}
                        </td>
                      </tr>
                    ))}
                    {g.vendedores.length === 0 && <tr><td colSpan="9" className="text-center text-muted">Sem vendas no período</td></tr>}
                  </tbody>
                </table>
              </div>
              {g.vendedores.length > 5 && (
                <div className="text-center mt-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandirGrupo(prev => ({ ...prev, [g.grupo]: !prev[g.grupo] }))}>
                    {expandirGrupo[g.grupo]
                      ? <><i className="bi bi-chevron-up me-1"></i>Mostrar só top 5</>
                      : <><i className="bi bi-chevron-down me-1"></i>Ver todos ({g.vendedores.length} vendedores)</>}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* RANKING — top N por venda no período, prêmio fixo por posição */}
          {isRanking && resultado?.resultado.map(g => {
            const porQtd = resultado.rankear_por === 'quantidade';
            const limite = Math.max(5, resultado.qtd_ganhadores || 3);
            return (
              <div key={g.grupo} className="card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <span className="badge-filial" style={{ fontSize: '0.9rem', padding: '6px 14px' }}><i className="bi bi-shop me-1"></i>{nomeGrupo(g.grupo)}</span>
                  <div style={{ background: '#fff8e1', padding: '8px 16px', borderRadius: 8, border: '2px solid #e0c86a' }}>
                    <small style={{ color: '#666' }}>PRÊMIO TOTAL</small>
                    <div className="fw-bold" style={{ color: '#8a6d00', fontSize: '1.2rem', lineHeight: 1 }}>R$ {fmt(g.total_premio)}</div>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead><tr><th>#</th><th>Vendedor</th><th style={{ textAlign: 'right' }}>{porQtd ? 'Quantidade' : 'Venda Líquida'}</th><th style={{ textAlign: 'right' }}>Prêmio</th></tr></thead>
                    <tbody>
                      {(expandirGrupo[g.grupo] ? g.vendedores : g.vendedores.slice(0, limite)).map(v => (
                        <tr key={v.codvend} style={{ background: v.ganhador ? '#fffbe9' : undefined, cursor: 'pointer' }} onClick={() => abrirDetalheVendedor(v.codvend, v.nome, nomeGrupo(g.grupo))}>
                          <td className="fw-bold">{v.posicao <= 3 ? ['🥇', '🥈', '🥉'][v.posicao - 1] : `${v.posicao}º`}</td>
                          <td><span style={{ textDecoration: 'underline dotted' }}>{v.nome}</span> <i className="bi bi-search ms-1" style={{ fontSize: '0.7rem', color: '#8a6d00' }}></i></td>
                          <td style={{ textAlign: 'right' }} className="fw-bold">{porQtd ? fmt(v.valor) : `R$ ${fmt(v.valor)}`}</td>
                          <td style={{ textAlign: 'right' }}>{v.ganhador ? <span className="badge" style={{ background: '#8a6d00', color: '#fff' }}>R$ {fmt(v.premio)}</span> : '—'}</td>
                        </tr>
                      ))}
                      {g.vendedores.length === 0 && <tr><td colSpan="4" className="text-center text-muted">Sem vendas no período</td></tr>}
                    </tbody>
                  </table>
                </div>
                {g.vendedores.length > limite && (
                  <div className="text-center mt-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandirGrupo(prev => ({ ...prev, [g.grupo]: !prev[g.grupo] }))}>
                      {expandirGrupo[g.grupo] ? <><i className="bi bi-chevron-up me-1"></i>Mostrar menos</> : <><i className="bi bi-chevron-down me-1"></i>Ver todos ({g.vendedores.length})</>}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL — detalhe analítico do vendedor */}
      {detalheVendedor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setDetalheVendedor(null)}>
          <div className="card p-3" style={{ width: '95%', maxWidth: 920, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
              <div>
                <h6 className="fw-bold mb-0"><i className="bi bi-person-lines-fill me-1"></i>{detalheVendedor.nome}</h6>
                <small className="text-muted">{detalheVendedor.grupo} · movimentos no período da campanha</small>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setDetalheVendedor(null)}><i className="bi bi-x-lg"></i></button>
            </div>

            {loadingDetalhe && <div className="text-center py-4"><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>}

            {!loadingDetalhe && detalheVendedor.dados && (() => {
              const d = detalheVendedor.dados;
              const t = d.totais || {};
              const un = (d.unidade_premiada || '').toLowerCase();
              // Campanha por quantidade (m²/un) mostra quantidade; por valor mostra R$.
              const isQuant = d.tipo === 'quantidade';
              const vVenda = isQuant ? `${fmt(t.quantVenda)} ${un}` : `R$ ${fmt(t.valorVenda)}`;
              const vDevol = isQuant ? `${fmt(t.quantDevol)} ${un}` : `R$ ${fmt(t.valorDevol)}`;
              const vCancel = isQuant ? `${fmt(t.quantCancel)} ${un}` : `R$ ${fmt(t.valorCancel)}`;
              const vLiq = isQuant ? `${fmt(t.quantLiquida)} ${un}` : `R$ ${fmt(t.valorLiquido)}`;
              return (
                <>
                  <div className="d-flex gap-2 flex-wrap mb-3">
                    <div style={{ background: '#e9f3ef', padding: '6px 14px', borderRadius: 8 }}>
                      <small className="text-muted d-block">Vendido</small>
                      <span className="fw-bold">{vVenda}</span>
                    </div>
                    <div style={{ background: '#fdeeee', padding: '6px 14px', borderRadius: 8 }}>
                      <small className="text-muted d-block">Devolvido</small>
                      <span className="fw-bold" style={{ color: '#b53d3d' }}>{vDevol}</span>
                    </div>
                    {t.quantCancel > 0 && (
                      <div style={{ background: '#f0f0f0', padding: '6px 14px', borderRadius: 8 }}>
                        <small className="text-muted d-block">Cancelado <i className="bi bi-info-circle" title="Itens cancelados — não entram no prêmio"></i></small>
                        <span className="fw-bold" style={{ color: '#888' }}>{vCancel}</span>
                      </div>
                    )}
                    <div style={{ background: 'var(--mostarda-claro)', padding: '6px 14px', borderRadius: 8 }}>
                      <small className="text-muted d-block">Líquido</small>
                      <span className="fw-bold">{vLiq}</span>
                    </div>
                    {t.premio != null && (
                      <div style={{ background: 'var(--mostarda)', color: '#fff', padding: '6px 14px', borderRadius: 8 }}>
                        <small style={{ opacity: 0.85 }} className="d-block">Prêmio</small>
                        <span className="fw-bold">R$ {fmt(t.premio)}</span>
                      </div>
                    )}
                  </div>

                  {d.linhas.length === 0 && <div className="text-muted text-center py-3">Sem movimentos no período.</div>}
                  {d.linhas.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                        <thead>
                          <tr>
                            <th>Foto</th><th>Produto</th><th>Documento</th><th>Data</th><th>Tipo</th>
                            <th style={{ textAlign: 'right' }}>Quant</th><th style={{ textAlign: 'right' }}>Preço</th><th style={{ textAlign: 'right' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.linhas.map((l, i) => {
                            const isVenda = l.tipo === 'venda';
                            const isCancel = l.tipo === 'cancelamento';
                            // Devolução = vermelho negativo; cancelamento = cinza informativo (não conta)
                            const corValor = isVenda ? undefined : (isCancel ? '#999' : '#b53d3d');
                            const sinal = isVenda || isCancel ? '' : '−';
                            return (
                              <tr key={i} style={{ opacity: isCancel ? 0.6 : 1 }}>
                                <td><ImagemProduto codpro={l.codpro} tamanho={44} clicavel alt={l.descricao} /></td>
                                <td><div className="fw-semibold">{l.codpro}</div><div className="text-muted" style={{ fontSize: '0.74rem' }}>{l.descricao}</div></td>
                                <td>{l.documento}</td>
                                <td>{l.data ? new Date(l.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}</td>
                                <td>
                                  {isVenda && <span className="badge bg-success" style={{ fontSize: '0.68rem' }}>Venda</span>}
                                  {l.tipo === 'devolucao' && <span className="badge bg-danger" style={{ fontSize: '0.68rem' }}>Devolução</span>}
                                  {isCancel && <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }} title="Cancelado — não conta no prêmio">Cancelado</span>}
                                </td>
                                <td style={{ textAlign: 'right', color: corValor }}>{sinal}{fmt(l.quant)} {(l.unid || '').toLowerCase()}</td>
                                <td style={{ textAlign: 'right' }}>R$ {fmt(l.preco)}</td>
                                <td style={{ textAlign: 'right', color: corValor }}>{sinal}R$ {fmt(l.valor)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
